<template>
<div class="payment-history" v-if="paymentsHistory">
  <div class="payment-history__title">
    <head-title class="head-title">Payment History</head-title>
    <router-link v-if="showViewAllLink" to="/payment-history" class="payment-history__view-all">
      <button-el red class="payment-history__view-all-btn payment-history__view-all-btn_desktop">
        View all
      </button-el>
      <icon-base red class="payment-history__view-all-btn payment-history__view-all-btn_mobile">
        <icon-arrow-btn/>
      </icon-base>
    </router-link>
  </div>
  <div class="table-wrapper">
    <table-el gray class="table-history">
      <tr slot="thead" class="table-history__trh">
        <th class="table-history__th table-history__th_date">Date</th>
        <th class="table-history__th table-history__th_description">Description</th>
        <th class="table-history__th table-history__th_period">Service period</th>
        <th class="table-history__th table-history__th_payment">Payment method</th>
        <th class="table-history__th table-history__th_subtotal">Subtotal</th>
        <th class="table-history__th table-history__th_total">Total</th>
        <th class="table-history__th table-history__th_file"></th>
      </tr>

      <tr slot="tbody" class="table-history__tr" :key="payment.paymentDate" v-for="payment in paymentsHistory.rows">
        <td class="table-history__td table-history__td_date">{{payment.paymentDate | format(shortDateFormat)}}</td>
        <td class="table-history__td table-history__td_description">{{payment.description}}</td>
        <td class="table-history__td table-history__td_period">{{payment.periodStart | format(shortDateFormat)}} — {{payment.periodEnd | format(shortDateFormat)}}</td>
        <td class="table-history__td table-history__td_payment">
          <icon-base black class="icon-card">
            <component :is="payment.cardBrand | getCardIcon" />
          </icon-base>
          <span class="card-number card-number_intable">**** **** **** {{payment.lastDigits}}</span>
        </td>
        <td class="table-history__td table-history__td_subtotal">{{payment.subtotal | formatMoney}} (+{{payment.tax | formatMoney}} tax)</td>
        <td class="table-history__td table-history__td_total">{{payment.total | formatMoney}}</td>
        <td class="table-history__td table-history__td_file">
          <red-text>
            <a title="Get invoice" class="link-file" :href="payment.invoiceURL" target="_blank">PDF</a>
          </red-text>
        </td>
      </tr>
    </table-el>
  </div>

  <div v-if="showViewAllLink" class="payment-history__notice">
    There are <router-link to="/payment-history">{{paymentsHistory.totalRowsCount | formatCountEntries}}</router-link>
  </div>
  <div v-else-if="!paymentsHistory.totalRowsCount" class="payment-history__notice">
    There are no entries yet...
  </div>
  <load-more-btn v-else-if="showLoadingMoreBtn" :disabled="loadingMore" class="load-more" @click="loadMore" :count="loadMoreCount"></load-more-btn>
</div>
</template>

<script>
import { format } from 'date-fns';
import { IconArrowBtn } from 'Icons';
import { formatMoney, formatCountEntries, getCardIcon, getAvailableCountForLoadingMore } from 'src/utils/helpers';
import { shortDateFormat, maxCountLoadingMoreBtn } from 'src/utils/config';

const componentRowsLimit = 3;
export default {
  filters: {
    format,
    formatMoney,
    formatCountEntries,
    getCardIcon,
  },
  components: {
    IconArrowBtn,
  },
  data: () => ({
    paymentsHistory: null,
    loadingMore: false,
  }),
  props: {
    componentMode: Boolean,
  },
  methods: {
    loadMore() {
      this.loadingMore = true;
      const offset = this.paymentsHistory.rows.length;
      this.$apiService.getPaymentHistory(maxCountLoadingMoreBtn, offset).then((data) => {
        this.paymentsHistory.rows = [...this.paymentsHistory.rows, ...data.rows];
      }).finally(() => {
        this.loadingMore = false;
      });
    },
  },
  computed: {
    formatCountEntries: () => formatCountEntries,
    shortDateFormat: () => shortDateFormat,
    showViewAllLink() {
      return !!(this.componentMode && this.paymentsHistory.totalRowsCount > componentRowsLimit);
    },
    showLoadingMoreBtn() {
      // eslint-disable-next-line max-len
      const hasUnloadedRows = this.paymentsHistory.totalRowsCount > this.paymentsHistory.rows.length;
      return !this.componentMode && hasUnloadedRows;
    },
    loadMoreCount() {
      return getAvailableCountForLoadingMore(this.paymentsHistory.totalRowsCount, this.paymentsHistory.rows.length); // eslint-disable-line max-len
    },
  },
  created() {
    const limit = this.componentMode ? componentRowsLimit : maxCountLoadingMoreBtn;
    this.$apiService.getPaymentHistory(limit, 0).then((paymentsHistory) => {
      this.paymentsHistory = paymentsHistory;
      this.$emit('load');
    });
  },
};
</script>
<style lang="scss" scoped>
$side-padding: 10px;
.payment-history {
  @include container;
  width: calc(100% - #{$side-padding * 2});
  background: $white-color2;
  border-radius: 5px;
  margin-top: 50px;
  .head-title {
    font-size: 17px;
    border-bottom: 0;
  }
  &__title {
    display: flex;
    align-items: center;
    height: 89px;
    padding: 0 30px;
  }
  &__view-all {
    margin-left: 34px;
    width: 150px;
  }
  &__view-all-btn {
    &_mobile {
      display: none;
    }
  }
  &__notice {
    border-top: 1px solid $white-color1;
    padding: 16px 31px 19px;
    @include font(13px, $grey-color9, 500);
    a {
      text-decoration: none;
      @include font(13px, $red-color3, 500);
    }
  }
}
$td-height: 60px;
.table-history {
  text-align: left;
  &__tr:not(:last-child) {
    border-bottom: 1px solid $white-color1;
  }
  &__th {
    padding: 12px 31px;
    &_period {
      padding: 12px 21px;
    }
  }
  &__td {
    height: $td-height;
    padding: 11px 31px;
    &_period {
      padding: 11px 21px;
    }
  }
  &__td,
  &__th {
    &_date {
      width: 150px;
    }
    &_description {
      width: 216px;
    }
    &_period {
      width: 230px;
    }
    &_payment {
      width: 237px;
      min-width: 209px;
    }
    &_subtotal {
      width: 172px;
    }
    &_total {
      width: 99px;
    }
    &_file {
      width: 105px;
    }
  }
  .icon-card {
    width: 24px;
    margin-right: 18px;
    vertical-align: middle;
  }
}
.link-file {
  text-decoration: none;
  color: $red-color3;
}
.load-more {
  padding: 30px !important;
  display: block;
}
@media screen and (max-width: 1180px) {
  .payment-history {
    position: relative;
    overflow-x: hidden;
  }
  .table-wrapper {
    overflow: auto;
  }
  .table-history {
    @include incrSelectorWeight(1) {
      position: static;
    }
    min-width: 1150px;
    $blur-size: 4px;
    &__trh,
    &__tr {
      &:before,
      &:after {
        content: '';
        position: absolute;
        width: 20px;
        height: $td-height - ($blur-size * 4);
        background-color: $white-color2;
        filter: blur($blur-size);
        z-index: 2;
        margin-top: $blur-size * 2;
      }
      $blur-side-margin: -9px;
      &:after {
        right: $blur-side-margin;
      }
      &:before {
        z-index: 3;
        left: $blur-side-margin;
      }
    }
    &__trh {
      &:before,
      &:after {
        background-color: $grey-color6;
        height: 33px;
        margin-top: 4px;
      }
    }
  }
}
@media screen and (max-width: 482px) {
  .payment-history {
    $side-padding: 5px;
    width: calc(100% - #{$side-padding * 2});
  }
}
@media screen and (max-width: 412px) {
  .payment-history {
    &__view-all {
      width: auto;
    }
    &__view-all-btn {
      &_mobile {
        display: flex;

      }
      &_desktop {
        display: none;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/common/PaymentsTable.vue